import React from 'react';
import { Button, Grid } from '@mui/material';
import { Formalite } from '@europrocurement/l2d-domain/reducers/formalites/slices/formalite';
import { Modalizer, FlexyHeaderForm } from '@europrocurement/flexy-components';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/system';
import { IconLookup, IconName } from '@fortawesome/sharp-regular-svg-icons';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import { CategorieCategorieRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { AnyAction } from '@reduxjs/toolkit';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    categoriesFormalitySelector,
    FormulaireFormalite,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { exactOnlyDigits } from '@europrocurement/l2d-modules/validation';
import { AppDispatch } from '../../../../redux/types';
import useFormalityFormHandler from '../../hooks/useFormalityFormHandler';
import useFormulaire from '../../hooks/useFormulaire';
import useCharge from '../../hooks/useCharge';
import useTypeRecupGreffe from '../../hooks/useTypeRecupGreffe';

type FormalityEditModalProps = {
    formality: Formalite;
    isOpen: boolean;
    onClose: () => void;
};

type FormData = {
    categorie: CategorieCategorieRead;
    informationsSupplementaires?: string | null;
    libelle?: string | null;
    rang: number | null;
    typeRecupGreffe?: string | null;
    charge: number | null;
    iconeName?: string | null;
    formulaire?: string | null;
};

const FormalityEditModal: React.FunctionComponent<FormalityEditModalProps> = function (props) {
    const { selectFormality } = useFormaliteService();
    const { formality, isOpen, onClose } = props;
    const { handleEditForm, requestState } = useFormalityFormHandler();
    const categoriesDatasource = useSelector(categoriesFormalitySelector);
    const { listFormulaires } = useFormulaire();
    const formulairesOptions = listFormulaires();
    const dispatch = useDispatch<AppDispatch>();
    const onSubmit = (formData: FormData) => {
        handleEditForm(formality, formData).then(() => {
            dispatch(selectFormality({ id: formality.id as number }) as unknown as AnyAction);
            onClose();
        });
    };
    const { charges } = useCharge();
    const { typesRecupGreffe } = useTypeRecupGreffe();

    const formStructure: FormStructure[] = [
        {
            type: 'text',
            name: 'libelle',
            inputlabel: 'Libellé',
            rules: {
                required: 'Vous devez renseigner un libellé',
            },
            placeholder: 'Le libellé de ma formalité',
        },
        {
            type: 'text',
            name: 'informationsSupplementaires',
            inputlabel: 'Informations supplémentaires',
            placeholder: 'Informations supplémentaires',
        },
        {
            type: 'number',
            name: 'rang',
            inputlabel: 'Rang',
            placeholder: 'Rang',
            inputProps: {
                step: 1,
                min: 0,
            },
            lg: 6,
            rules: {
                validate: {
                    ...exactOnlyDigits('rang'),
                },
            },
        },
        {
            type: 'select',
            name: 'categorie',
            inputlabel: 'Catégorie',
            getOptionLabel: ({ libelle }: { libelle: string }) => libelle,
            getOptionValue: ({ id }: { id: number }) => id.toString(),
            options: categoriesDatasource.data,
            placeholder: 'Veuillez sélectionner une catégorie',
            rules: {
                required: 'Vous devez sélectionner une catégorie',
            },
            lg: 6,
        },
        {
            type: 'text',
            name: 'iconeName',
            inputlabel: 'Icône (https://fontawesome.com/search)',
            placeholder: 'Icône',
            rules: {
                validate: {
                    isAnIcon: (value) => {
                        if (!value || value === '') {
                            return true;
                        }
                        const icon = findIconDefinition({ iconName: value } as IconLookup);
                        if (icon) {
                            return true;
                        }
                        return "L'icône est invalide";
                    },
                },
            },
            xs: 12,
            sm: 12,
            md: 9,
            lg: 9,
        },
        {
            type: 'customItem',
            name: 'renderIcone',
            xs: 12,
            sm: 12,
            md: 3,
            lg: 3,
            renderField: (formContext) => {
                let iconeRender: string | null = formContext.getValues('iconeName');
                if (!iconeRender || !findIconDefinition({ iconName: iconeRender } as IconLookup)) {
                    iconeRender = 'image-slash';
                }
                console.log(iconeRender);
                return (
                    <Box
                        sx={{
                            fontSize: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '15px',
                        }}
                    >
                        <FaOptionIcon icon={['fad', iconeRender as IconName]} />
                    </Box>
                );
            },
        },
        {
            type: 'select',
            name: 'formulaire',
            inputlabel: 'Formulaire',
            options: formulairesOptions,
            getOptionLabel: ({ libelle }: { libelle: string }) => libelle,
            getOptionValue: ({ id }: { id: string }) => id.toString(),
            placeholder: 'Veuillez sélectionner un formulaire',
        },
        {
            type: 'select',
            name: 'charge',
            inputlabel: 'Charge',
            options: charges,
            placeholder: 'Veuillez sélectionner une charge',
            rules: {
                required: 'Vous devez sélectionner une charge',
            },
        },
        {
            type: 'select',
            name: 'typeRecupGreffe',
            inputlabel: 'Récupération du greffe',
            options: typesRecupGreffe,
            placeholder: 'Veuillez sélectionner un type de récupération du greffe',
            rules: {
                required: 'Vous devez sélectionner un type de récupération du greffe',
            },
        },
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onTouched',
        defaultValues: {
            libelle: formality?.libelle,
            informationsSupplementaires: formality?.informationsSupplementaires,
            iconeName: formality?.iconeName,
            categorie: categoriesDatasource.data.find(
                (categorie: CategorieCategorieRead) => categorie.id === formality?.categorie?.id,
            ),
            formulaire: formulairesOptions.find(
                (formulaire: FormulaireFormalite) => formulaire.id === formality?.formulaire,
            ),
            rang: formality?.rang,
            charge: charges.find((charge) => charge.value === formality.charge),
            typeRecupGreffe: typesRecupGreffe.find(
                (typeRecupGreffe) => typeRecupGreffe.value === formality.typeRecupGreffe,
            ),
        },
    });

    const formProps = {
        formContext,
        formObject: {},
        onSubmit,
        submitButton: {
            displayed: false,
        },
    };

    formContext.watch(['iconeName']);

    const submitBtn = (
        <Button
            key="formality-edit-submit_button"
            color="primary"
            variant="contained"
            onClick={formContext.handleSubmit(onSubmit)}
            style={{ marginTop: '8px', marginBottom: '8px' }}
            disabled={requestState.isProcessing}
        >
            Enregistrer
        </Button>
    );
    const cancelBtn = (
        <Button
            key="formality-edit-cancel_button"
            color="primary"
            variant="outlined"
            onClick={onClose}
            style={{ marginTop: '8px', marginBottom: '8px' }}
            disabled={requestState.isProcessing}
        >
            Annuler
        </Button>
    );

    return (
        <Modalizer
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            modalActions={[cancelBtn, submitBtn]}
        >
            <Grid>
                <FlexyHeaderForm
                    outlined
                    label="Modifier une formalité"
                />
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Grid>
        </Modalizer>
    );
};

export default FormalityEditModal;
